import { Translate } from 'next-translate';
import { ChapterForButton, ChapterState } from '@customTypes/chapter';
import { LastPlayedStory } from '@customTypes/story';
import { formatWaitingTime, convertCharName } from '@common/utils';
import {
  getRemoteConfig,
  fetchAndActivate,
  getBoolean,
} from 'firebase/remote-config';
import { firebaseApp } from '@lib/firebase';

export interface IViewData {
  storyId: number;
  storyName: string;
  chapterId: number;
  chapterIndex: number;
  chapterPrice: number;
  mainImageUrl?: string;
  price: number;
  storyPrice: number;
  discountRate: number;
  originalPrice: number;
  chapterName: string;
  hasGraphIcon: boolean;
  hasNewBadge: boolean;
  hasBGM: boolean;
  subText: string;
  endingText: string;
  subTextColor: string;
  isLocked: boolean;
  isGray: boolean;
  isDisable: boolean;
  toastMessage: string;
  showEndingView: boolean;
  showStat: boolean;
  showTimeLeap: boolean;
  thumbnail?: string;
  endingId?: number;
  coinForRestart?: number;
  needWholePurchase: boolean;
  introImageUrl?: string;
  freedAt?: string | null;
  freeDependencyChapterIndex?: number | null;
  isTimeLeapFree: boolean;
  purchasable?: boolean;
  purchased?: boolean;
  hasAdultChapter?: boolean;
  showPrice?: boolean;
  shouldShowTimeLeapOnChapterClick?: boolean;
}

interface IGetChapterType {
  data: ChapterForButton;
  playingChapterIds: number[];
  endingChapterIds: number[];
  isWholePurchase: boolean;
  isWholePurchased: boolean;
  lastPreviewIndex?: number | null;
  finalEndingChapterIndex?: number | null;
}

export const checkAdEnabled = ({ storyId }: { storyId: number }) => {
  const remoteConfig = getRemoteConfig(firebaseApp);

  return fetchAndActivate(remoteConfig)
    .then(() => {
      return getBoolean(remoteConfig, `admob_enabled_story_${storyId}`);
    })
    .catch(() => {
      return false;
    });
};

export const getChapterType = ({
  data,
  playingChapterIds = [],
  endingChapterIds = [],
  finalEndingChapterIndex,
  isWholePurchase,
  isWholePurchased,
  lastPreviewIndex = 0,
}: IGetChapterType): ChapterState => {
  const isFree = data?.freedAt !== null || data.price === 0;

  const isWaitingTimeZero =
    data?.freeDependencyChapterIndex !== null &&
    data?.freeDependencyPeriod !== null &&
    data?.remainingTimeToFree === 0 &&
    isFree;
  const isEnding = endingChapterIds.find((id) => id === data.chapterId);
  const isPlaying = playingChapterIds.find((id) => id === data.chapterId);
  const isAllChapterPurchased = data?.userPurchasedChapter !== null;

  let chapterType: ChapterState = 'NEED_PURCHASE';
  if (isPlaying) {
    if (isEnding) {
      chapterType = 'DONE';

      if (finalEndingChapterIndex) {
        // 최종화 엔딩 도달, 최종화 엔딩 도달 회차의 이후 회차인 경우 dismiss
        if (data.chapterIndex > finalEndingChapterIndex) {
          chapterType = isFree ? 'DISMISS' : 'DISMISS_AND_NEED_PURCHASED';
        } else {
          // 최종화 엔딩 도달, 최종화 엔딩 도달 회차의 이전 회차인 경우 done
          chapterType = 'DONE';
        }
      }
    } else {
      chapterType = 'READING';
    }
  } else {
    if (
      isFree ||
      isWholePurchase ||
      isWaitingTimeZero ||
      isAllChapterPurchased
    ) {
      chapterType = 'ACTIVE';
    }
  }

  // 통구매작품이고, 사용자가 아직 구매 안했고, lastPreviewIndex가 chapterIndex보다 작으면
  if (
    isWholePurchase &&
    !isWholePurchased &&
    lastPreviewIndex &&
    lastPreviewIndex < data.chapterIndex
  ) {
    chapterType = 'WHOLE_AND_NEED_PURCHASED';
  }

  if (finalEndingChapterIndex && data.chapterIndex > finalEndingChapterIndex) {
    chapterType = isFree ? 'DISMISS' : 'DISMISS_AND_NEED_PURCHASED';
  }

  return chapterType;
};

interface IGetChapterViewData {
  t: Translate;
  storyId: number;
  price: number;
  storyPrice: number;
  discountRate: number;
  originalPrice: number;
  storyName: string;
  chapters: ChapterForButton[];
  chapter: ChapterForButton;
  isWholePurchase: boolean;
  isWholePurchased: boolean;
  lastPreviewIndex?: number | null;
  lastPlay?: LastPlayedStory | null;
  coinForRestart?: number;
  thumbnail?: string;
  introImageUrl?: string;
  mainImageUrl?: string;
  purchasable?: boolean;
  purchased?: boolean;
  hasAdultChapter?: boolean;
  isStoryGame?: boolean;
}

export const getChapterViewData = ({
  t,
  storyId,
  storyName,
  price,
  discountRate,
  storyPrice,
  originalPrice,
  chapters,
  chapter,
  lastPlay,
  coinForRestart,
  thumbnail,
  isWholePurchase,
  isWholePurchased,
  lastPreviewIndex,
  introImageUrl,
  mainImageUrl,
  purchasable,
  purchased,
  hasAdultChapter,
  isStoryGame,
}: IGetChapterViewData): IViewData => {
  const userPlayedChapters = lastPlay ? lastPlay?.playedChapters : [];
  const endingChapterIds = userPlayedChapters
    ?.filter((chapter) => chapter.ending)
    .map((chapter) => chapter.chapterId);
  const finalEnding = userPlayedChapters?.find(
    (chapter) => chapter.ending?.isFinal === true,
  );
  const finalEndingChapterIndex = chapters.find(
    (chapter) => chapter.chapterId === finalEnding?.chapterId,
  )?.chapterIndex;
  const endingId = endingChapterIds.find((id) => id === chapter.chapterId);

  const playingChapterIds = userPlayedChapters?.map(
    (chapter) => chapter.chapterId,
  );
  const isPlaying = playingChapterIds.find((id) => id === chapter.chapterId);
  const type = getChapterType({
    data: chapter,
    playingChapterIds,
    endingChapterIds,
    finalEndingChapterIndex,
    isWholePurchase,
    isWholePurchased,
    lastPreviewIndex,
  });

  const viewData: IViewData = {
    thumbnail,
    introImageUrl,
    chapterId: chapter?.chapterId,
    chapterPrice: chapter?.price,
    chapterIndex: chapter?.chapterIndex,
    mainImageUrl,
    storyId,
    price,
    storyPrice,
    originalPrice,
    discountRate,
    storyName,
    chapterName: chapter?.name,
    hasGraphIcon: false,
    hasNewBadge: false,
    hasBGM: false,
    subText: '',
    endingText: '',
    subTextColor: 'gray600',
    isLocked: false,
    isGray: false,
    isDisable: false,
    toastMessage: '',
    showEndingView: false,
    showStat: false,
    showTimeLeap: false,
    coinForRestart: coinForRestart,
    needWholePurchase: false,
    freedAt: chapter?.freedAt,
    freeDependencyChapterIndex: chapter?.freeDependencyChapterIndex,
    isTimeLeapFree: false,
    purchasable,
    purchased,
    hasAdultChapter,
    showPrice: false,
    shouldShowTimeLeapOnChapterClick: false,
  };

  if (chapter?.hasBGM) {
    viewData.hasBGM = true;
  }

  if (chapter?.hasGraphIcon) {
    viewData.hasGraphIcon = true;

    if (endingId) {
      viewData.showStat = true;
      viewData.endingId = endingId;
    }
  }

  if (chapter?.hasNewBadge) {
    viewData.hasNewBadge = true;
  }

  if (chapter?.isTimeLeapFree) {
    viewData.isTimeLeapFree = true;
  }

  if (type !== 'READING') {
    if (chapter?.chapterIndex > 1) {
      const firstChapter = chapters.find((item) => item.chapterIndex === 1);
      const currentPlayingChapter = chapters.find(
        (item) => item.chapterId === lastPlay?.currentPlayingChapter.chapterId,
      );
      const isCurrentChapterEnding = !!lastPlay?.currentPlayingChapter.ending;
      const nextChapter = chapters.find(
        (item) =>
          lastPlay &&
          item.chapterIndex ===
            lastPlay.currentPlayingChapter.chapter.chapterIndex + 1,
      );

      if (
        isCurrentChapterEnding &&
        chapter.chapterId !== nextChapter?.chapterId
      ) {
        viewData.toastMessage = `${t(
          'storydetail_popup_description_start_with_chapter_name',
          {
            value: nextChapter?.name,
          },
        )}`;
      }

      if (
        !isCurrentChapterEnding &&
        chapter.chapterId !== firstChapter?.chapterId
      ) {
        viewData.toastMessage = `${t(
          'storydetail_popup_description_start_with_chapter_name',
          {
            value: currentPlayingChapter
              ? currentPlayingChapter.name
              : firstChapter?.name,
          },
        )}`;
      }
    }
  }

  if (type === 'ACTIVE') {
    // 첫번째 기스무 챕터 인덱스
    const firstIndexOfWaiting = chapters.find(
      (chapter) => chapter.freeDependencyChapterIndex,
    )?.chapterIndex;

    if (
      firstIndexOfWaiting &&
      chapter.chapterIndex === firstIndexOfWaiting - 1
    ) {
      viewData.subText = t('storydetail_screen_label_free_for_now');
      viewData.subTextColor = 'primary500';
    }
    if (
      chapter?.freeDependencyPeriod !== null &&
      chapter?.remainingTimeToFree === 0
    ) {
      // 지금 보면 무료 백로그 처리
      viewData.subText = t('storydetail_screen_label_free_for_now');
      viewData.subTextColor = 'primary500';
    }
  } else if (type === 'READING') {
    viewData.isGray = true;
    viewData.showTimeLeap = true;
  } else if (type === 'DONE') {
    viewData.showTimeLeap = true;
    viewData.toastMessage = t('storydetail_popup_description_already_played');
    viewData.isGray = true;

    const endingChapter = userPlayedChapters?.filter(
      (data) => data.chapterId === chapter.chapterId,
    )[0];

    if (lastPlay) {
      viewData.endingText = convertCharName(
        endingChapter?.ending?.displayName || endingChapter?.ending?.name || '',
        lastPlay.firstName || '',
        lastPlay.lastName || '',
      );
    }

    if (finalEndingChapterIndex === chapter.chapterIndex) {
      viewData.showEndingView = true;
    }
  } else if (type === 'NEED_PURCHASE') {
    viewData.isLocked = true;

    if (chapter.freeDependencyPeriod === null) {
      // 기스무 아닌 챕터(기간 후 무료 회차)
      if (chapter.remainingTimeToFree && chapter.remainingTimeToFree > 0) {
        viewData.subText = formatWaitingTime({
          seconds: chapter.remainingTimeToFree,
          t,
        });
      }
    } else {
      //기스무 챕터
      if (chapter.remainingTimeToFree && chapter.remainingTimeToFree > 0) {
        viewData.subText = formatWaitingTime({
          seconds: chapter.remainingTimeToFree,
          t,
        });
        viewData.subTextColor = 'primary500';
      } else if (
        chapter.remainingTimeToFree &&
        chapter.remainingTimeToFree === 0
      ) {
        viewData.subText = t('storydetail_screen_label_free_for_now');
        viewData.subTextColor = 'primary500';
      } else {
        viewData.subText = t(
          'storydetail_screen_label_description_free_after_day',
          {
            value: '?',
          },
        );
      }
    }
  } else if (type === 'WHOLE_AND_NEED_PURCHASED') {
    viewData.isLocked = true;
    viewData.needWholePurchase = true;

    if (isPlaying) {
      viewData.isGray = true;
      viewData.showTimeLeap = true;
    }
  } else if (type === 'DISMISS_AND_NEED_PURCHASED') {
    viewData.isGray = true;
    viewData.isDisable = true;
    viewData.isLocked = true;
    viewData.showTimeLeap = false;

    const endingChapter = userPlayedChapters?.filter(
      (data) => data.chapterId === chapter.chapterId,
    )[0];

    if (lastPlay) {
      viewData.endingText = convertCharName(
        endingChapter?.ending?.displayName || endingChapter?.ending?.name || '',
        lastPlay.firstName || '',
        lastPlay.lastName || '',
      );
    }

    if (chapter.remainingTimeToFree && chapter.remainingTimeToFree > 0) {
      // 조기엔딩일 경우여도 기간무는 n 분후 무료 표시
      viewData.subText = formatWaitingTime({
        seconds: chapter.remainingTimeToFree,
        t,
      });
    }

    if (finalEndingChapterIndex === chapter.chapterIndex) {
      viewData.showEndingView = true;
    }
  } else if (type === 'DISMISS') {
    viewData.isGray = true;
    viewData.isDisable = true;

    const endingChapter = userPlayedChapters?.filter(
      (data) => data.chapterId === chapter.chapterId,
    )[0];
    if (lastPlay) {
      viewData.endingText = convertCharName(
        endingChapter?.ending?.displayName || endingChapter?.ending?.name || '',
        lastPlay.firstName || '',
        lastPlay.lastName || '',
      );
    }

    if (finalEndingChapterIndex === chapter.chapterIndex) {
      viewData.showEndingView = true;
    }
  }

  // 스토리 게임 작품인 경우 일부 조건이 변경된다. 아래에서 변경된 조건을 재정의한다.
  if (isStoryGame) {
    if (type === 'DONE') {
      viewData.toastMessage = '';
      viewData.shouldShowTimeLeapOnChapterClick = true;
    }

    // 회차 완료 후 엔딩명을 노출하지 않는다.
    viewData.endingText = '';
  }

  return viewData;
};
